import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormControl } from "@angular/forms";
import { Router } from "@angular/router";
import { forkJoin, takeUntil } from "rxjs";

import {
  BillingCheckoutResponse,
  BillingRecordSummary,
  BillingStatus,
  Organization,
  SubscriptionPlanCatalogItem,
  SubscriptionPlanVersion,
} from "../../organizations/models/organizations.entity";
import { OrganizationRepository } from "../../organizations/repositories/organization.repository";
import { ManagedOfferCheckoutService } from "../../organizations/services/managed-offer-checkout.service";
import { DialogComponent } from "../../shared/components/dialog/dialog.component";
import { DialogRef } from "../../shared/components/dialog/dialog.ref";
import { DIALOG_DATA } from "../../shared/components/dialog/dialog.token";
import { SnackbarService } from "../../shared/components/snackbar/snackbar.service";
import { OfferPlanCode } from "../../shared/types/offer-upsell";

type WizardOrganizationOption = {
  id: string;
  name: string;
};

type WizardStep = "organization" | "offer" | "summary";

export type OfferActivationWizardData = {
  organizations: WizardOrganizationOption[];
  preselectedPlanCode?: OfferPlanCode;
  selectedOrganizationId?: string;
};

export type OfferActivationWizardResult = {
  activatedOrganizationId?: string;
  checkoutStarted?: boolean;
};

@Component({
  selector: "app-offer-activation-wizard",
  templateUrl: "./offer-activation-wizard.component.html",
  styleUrls: [ "./offer-activation-wizard.component.scss" ],
})
export class OfferActivationWizardComponent
  extends DialogComponent<OfferActivationWizardResult, OfferActivationWizardData>()
  implements OnInit, OnDestroy {
  readonly managedPlanCodes = this.managedOfferCheckoutService.getManagedPlanCodes();

  readonly organizationControl = new FormControl("", { nonNullable: true });

  readonly planControl = new FormControl("", { nonNullable: true });

  readonly seatCountControl = new FormControl(1, { nonNullable: true });

  readonly managedPlanNames: string[] = [];

  currentStepIndex = 0;

  transitionDirection: "forward" | "backward" = "forward";

  loadingCatalog = true;

  loadingOrganization = false;

  submitting = false;

  organization?: Organization;

  billingStatus: BillingStatus | null = null;

  errorMessage?: string;

  private readonly organizationsByLabel: Record<string, WizardOrganizationOption> = {};

  private readonly managedPlansByName: Record<string, SubscriptionPlanCatalogItem> = {};

  constructor(
    @Inject(DIALOG_DATA) public override data: OfferActivationWizardData,
    public readonly ref: DialogRef<OfferActivationWizardResult>,
    private readonly organizationRepository: OrganizationRepository,
    private readonly managedOfferCheckoutService: ManagedOfferCheckoutService,
    private readonly snackBarService: SnackbarService,
    private readonly router: Router,
  ) {
    super(ref, data);
  }

  ngOnInit(): void {
    super.onInit();

    this.hydrateOrganizationChoices();
    this.loadCatalog();

    const initialOrganization = this.getInitialOrganizationChoice();
    if (initialOrganization) {
      this.organizationControl.setValue(initialOrganization.name);
      this.loadOrganizationContext(initialOrganization.id);
    }
  }

  ngOnDestroy(): void {
    super.onDestroy();
  }

  get steps(): WizardStep[] {
    return this.requiresOrganizationStep ? [ "organization", "offer", "summary" ] : [ "offer", "summary" ];
  }

  get currentStep(): WizardStep {
    return this.steps[this.currentStepIndex] ?? this.steps[0];
  }

  get currentStepTitle(): string {
    if (this.currentStep === "organization") {
      return "Organisation";
    }

    if (this.currentStep === "offer") {
      return "Offre et nombre de collaborateurs";
    }

    return "Récapitulatif";
  }

  get currentStepDescription(): string {
    if (this.currentStep === "organization") {
      return "Choisissez l’organisation qui portera l’offre et la facturation.";
    }

    if (this.currentStep === "offer") {
      return "Cadrez l’offre, le nombre de collaborateurs et les conditions d’activation.";
    }

    return "Vérifiez les éléments de facturation avant de lancer l’activation.";
  }

  get requiresOrganizationStep(): boolean {
    return this.organizationOptions.length > 1;
  }

  get organizationOptions(): WizardOrganizationOption[] {
    return this.data.organizations ?? [];
  }

  get organizationLabels(): string[] {
    return Object.keys(this.organizationsByLabel);
  }

  get selectedOrganizationChoice(): WizardOrganizationOption | undefined {
    return this.organizationsByLabel[this.organizationControl.value];
  }

  get currentOrganizationName(): string {
    return this.organization?.name ?? this.selectedOrganizationChoice?.name ?? "Organisation";
  }

  get currentOfferName(): string {
    const currentPlanCode = this.organization?.activeSubscription?.planCode as OfferPlanCode | undefined;
    return currentPlanCode
      ? this.managedOfferCheckoutService.getPlanDisplayName(currentPlanCode)
      : this.organization?.subscriptionPlan?.name ?? "Aucune offre active";
  }

  get hasActiveManagedOffer(): boolean {
    return !!this.organization?.activeSubscription;
  }

  get pendingSubscriptionCheckout(): BillingRecordSummary | null {
    return this.billingStatus?.pendingSubscriptionCheckout ?? null;
  }

  get pendingSubscriptionCheckoutUrl(): string | null {
    return this.pendingSubscriptionCheckout?.checkoutUrl ?? null;
  }

  get selectedManagedPlan(): SubscriptionPlanCatalogItem | undefined {
    return this.managedPlansByName[this.planControl.value];
  }

  get selectedManagedPlanCode(): OfferPlanCode | undefined {
    return this.selectedManagedPlan?.slug as OfferPlanCode | undefined;
  }

  get selectedManagedPlanLatestVersion(): SubscriptionPlanVersion | undefined {
    return this.managedOfferCheckoutService.resolveCatalogPlanVersion(
      this.selectedManagedPlanCode,
      this.selectedManagedPlan,
    );
  }

  get selectedSeatCount(): number {
    return this.managedOfferCheckoutService.normalizePositiveNumber(this.seatCountControl.value, 1);
  }

  get selectedAnnualPricePerSeatCents(): number {
    return this.managedOfferCheckoutService.getAnnualPricePerSeatCents(
      this.selectedManagedPlanCode,
      this.selectedSeatCount,
      this.selectedManagedPlanLatestVersion,
    );
  }

  get selectedAnnualAmountCents(): number {
    return this.selectedAnnualPricePerSeatCents * this.selectedSeatCount;
  }

  get selectedIncludedJourneysCount(): number {
    return this.managedOfferCheckoutService.getIncludedJourneysCount(
      this.selectedManagedPlanLatestVersion,
      this.selectedSeatCount,
      this.organization,
    );
  }

  get selectedComplementaryJourneyPriceCents(): number {
    return this.managedOfferCheckoutService.getComplementaryJourneyPriceCents(
      this.selectedManagedPlanLatestVersion,
      this.organization,
    );
  }

  get selectedOfferNextRenewalLabel(): string {
    return this.formatDate(
      this.managedOfferCheckoutService.getNextRenewalDate(this.managedOfferCheckoutService.getTodayDate()),
    );
  }

  get canGoNext(): boolean {
    if (this.currentStep === "organization") {
      return !!this.selectedOrganizationChoice && !this.loadingOrganization;
    }

    if (this.currentStep === "offer") {
      return !!this.organization
        && !!this.selectedManagedPlanLatestVersion
        && this.selectedSeatCount > 0
        && !this.loadingCatalog
        && !this.loadingOrganization;
    }

    return false;
  }

  get canConfirmActivation(): boolean {
    return !!this.organization?.id
      && !!this.selectedManagedPlanCode
      && this.selectedSeatCount > 0
      && !this.submitting
      && !this.loadingOrganization
      && !this.hasActiveManagedOffer;
  }

  next(): void {
    if (this.currentStep === "organization") {
      const selectedOrganization = this.selectedOrganizationChoice;
      if (!selectedOrganization) {
        return;
      }

      this.loadOrganizationContext(selectedOrganization.id, () => {
        this.transitionDirection = "forward";
        this.currentStepIndex = Math.min(this.currentStepIndex + 1, this.steps.length - 1);
      });
      return;
    }

    if (this.currentStep === "offer" && this.canGoNext) {
      this.transitionDirection = "forward";
      this.currentStepIndex = Math.min(this.currentStepIndex + 1, this.steps.length - 1);
    }
  }

  previous(): void {
    this.transitionDirection = "backward";
    this.currentStepIndex = Math.max(this.currentStepIndex - 1, 0);
  }

  submit(): void {
    if (!this.organization?.id || !this.selectedManagedPlanCode) {
      return;
    }

    if (this.pendingSubscriptionCheckoutUrl) {
      this.openCheckout(this.pendingSubscriptionCheckoutUrl);
      this.snackBarService.pushMessage("Le lien de paiement Sellsy a été ouvert", "success");
      this.close({
        checkoutStarted: true,
      });
      return;
    }

    this.errorMessage = undefined;
    this.submitting = true;

    this.organizationRepository.createSubscriptionCheckout(this.organization.id, {
      planCode: this.selectedManagedPlanCode,
      seatCount: this.selectedSeatCount,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.submitting = false;
          this.handleCheckoutResponse(response);
        },
        error: (error) => {
          this.submitting = false;
          const apiMessage = error?.error?.message ?? error?.error ?? error?.message;

          if (apiMessage === "managed_subscription_already_active") {
            this.errorMessage = "Cette organisation dispose déjà d'une offre managée active. Le changement d'offre reste à traiter depuis l'espace pilote.";
            return;
          }

          this.errorMessage = "Le checkout Sellsy n'a pas pu être préparé pour cette offre.";
        },
      });
  }

  goToOrganizationUnits(): void {
    if (!this.organization?.id) {
      return;
    }

    this.close();
    setTimeout(() => {
      this.router.navigate([ "organizations", this.organization?.id, "units" ]);
    }, 500);
  }

  override close(payload?: OfferActivationWizardResult): void {
    super.close(payload);
  }

  formatCurrency(amountCents: number): string {
    return this.managedOfferCheckoutService.formatCurrency(amountCents);
  }

  formatDate(date?: Date | string | null): string {
    if (!date) {
      return "Non disponible";
    }

    return new Date(date).toLocaleDateString();
  }

  getStepLabel(step: WizardStep): string {
    if (step === "organization") {
      return "Organisation";
    }

    if (step === "offer") {
      return "Offre & nombre de collaborateurs";
    }

    return "Récapitulatif";
  }

  private hydrateOrganizationChoices(): void {
    const labelCounts: Record<string, number> = {};
    const uniqueOrganizations = this.organizationOptions.reduce((acc, organization) => {
      if (!organization?.id || acc.some((item) => item.id === organization.id)) {
        return acc;
      }

      acc.push(organization);
      return acc;
    }, [] as WizardOrganizationOption[]);

    uniqueOrganizations.forEach((organization) => {
      const duplicateCount = labelCounts[organization.name] ?? 0;
      labelCounts[organization.name] = duplicateCount + 1;
      const label = duplicateCount ? `${organization.name} (${duplicateCount + 1})` : organization.name;

      this.organizationsByLabel[label] = organization;
    });

    this.data.organizations = uniqueOrganizations;
  }

  private getInitialOrganizationChoice(): WizardOrganizationOption | undefined {
    const { selectedOrganizationId } = this.data;
    if (selectedOrganizationId) {
      return this.organizationOptions.find((organization) => organization.id === selectedOrganizationId);
    }

    if (this.organizationOptions.length === 1) {
      return this.organizationOptions[0];
    }

    return undefined;
  }

  private loadCatalog(): void {
    this.organizationRepository.getSubscriptionPlans()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (plans) => {
          this.managedOfferCheckoutService.setCatalog(plans);
          const managedPlans = this.managedPlanCodes
            .map((planCode) => plans.find((plan) => plan.slug === planCode))
            .filter((plan): plan is SubscriptionPlanCatalogItem => !!plan);

          this.managedPlanNames.length = 0;
          managedPlans.forEach((plan) => {
            const planLabel = this.managedOfferCheckoutService.getPlanDisplayName(plan.slug as OfferPlanCode);
            this.managedPlansByName[planLabel] = plan;
            this.managedPlanNames.push(planLabel);
          });

          this.loadingCatalog = false;
          this.syncOfferControls();
        },
        error: () => {
          this.loadingCatalog = false;
          this.errorMessage = "Le catalogue des offres n'a pas pu être chargé.";
        },
      });
  }

  private loadOrganizationContext(organizationId: string, callback?: () => void): void {
    if (!organizationId) {
      return;
    }

    if (this.organization?.id === organizationId && this.billingStatus) {
      this.syncOfferControls();
      callback?.();
      return;
    }

    this.loadingOrganization = true;
    this.errorMessage = undefined;

    forkJoin({
      organization: this.organizationRepository.findById(organizationId),
      billingStatus: this.organizationRepository.getBillingStatus(organizationId),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ organization, billingStatus }) => {
          this.organization = organization;
          this.billingStatus = billingStatus;
          this.loadingOrganization = false;
          this.syncOfferControls();
          callback?.();
        },
        error: () => {
          this.loadingOrganization = false;
          this.errorMessage = "Le contexte de facturation de cette organisation n'a pas pu être chargé.";
        },
      });
  }

  private syncOfferControls(): void {
    if (this.loadingCatalog || !this.organization) {
      return;
    }

    const currentPlanCode = this.organization.activeSubscription?.planCode as OfferPlanCode | undefined;
    const selectedPlanCode = this.data.preselectedPlanCode
      ?? (currentPlanCode && this.managedPlanCodes.includes(currentPlanCode)
        ? currentPlanCode
        : this.managedPlanCodes[0]);

    this.planControl.setValue(this.managedOfferCheckoutService.getPlanDisplayName(selectedPlanCode), { emitEvent: false });
    this.seatCountControl.setValue(
      this.organization.activeSubscription?.seatCount ?? Math.max(this.organization.members?.length ?? 0, 1),
      { emitEvent: false },
    );
  }

  private handleCheckoutResponse(response: BillingCheckoutResponse): void {
    if (response.checkoutUrl) {
      this.openCheckout(response.checkoutUrl);
      this.snackBarService.pushMessage("Le lien de paiement Sellsy a été ouvert", "success");
      this.close({
        checkoutStarted: true,
      });
      return;
    }

    this.snackBarService.pushMessage("Votre offre a bien été activée", "success");
    this.close({
      activatedOrganizationId: this.organization?.id,
    });
  }

  private openCheckout(url?: string | null): void {
    if (!url) {
      return;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  }
}
